import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { api, ApiError, type Movement, type Reason, type Warehouse } from '../api/client'
import { Button, EmptyState, Input, Notice, Select, Spinner } from '../ui/controls'

/**
 * Журнал движений (п. 4.16).
 *
 * Хронологический перечень всех движений с отбором по периоду, складу,
 * причине и пользователю. Журнал доступен только на добавление, поэтому
 * правки записей здесь нет по определению.
 */
export default function MovementsPage() {
  const nav = useNavigate()
  const [rows, setRows] = useState<Movement[] | null>(null)
  const [total, setTotal] = useState(0)
  const [warehouses, setWarehouses] = useState<Warehouse[]>([])
  const [reasons, setReasons] = useState<Reason[]>([])
  const [notice, setNotice] = useState<string | null>(null)
  const [reconcile, setReconcile] = useState<{ consistent: boolean; count: number } | null>(null)

  const [filter, setFilter] = useState<Record<string, string | number>>({ page: 1, limit: 50 })

  const reload = useCallback(async (f: Record<string, string | number>) => {
    try {
      const res = await api.movements(f)
      setRows(res.movements ?? [])
      setTotal(res.total)
    } catch (e) {
      setNotice(e instanceof ApiError ? e.message : 'Не удалось загрузить журнал')
      setRows([])
    }
  }, [])

  useEffect(() => {
    const t = setTimeout(() => void reload(filter), 200)
    return () => clearTimeout(t)
  }, [filter, reload])

  useEffect(() => {
    void api.warehouses().then(setWarehouses)
    void api.reasons().then(setReasons)
  }, [])

  function patch(p: Record<string, string | number>) {
    setFilter((f) => ({ ...f, ...p, page: p.page ?? 1 }))
  }

  const pages = Math.max(1, Math.ceil(total / Number(filter.limit ?? 50)))

  return (
    <section>
      <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-ink-900 dark:text-ink-50">Журнал движений</h2>
          <p className="mt-0.5 text-sm text-ink-500">
            Движений: {total}. Записи журнала не изменяются — исправление выполняется
            корректировкой.
          </p>
        </div>
        <Button
          size="sm"
          onClick={async () => {
            const res = await api.reconcile()
            setReconcile({ consistent: res.consistent, count: res.discrepancies.length })
          }}
        >
          Сверить с остатками
        </Button>
      </header>

      {notice && <Notice kind="error" onClose={() => setNotice(null)}>{notice}</Notice>}

      {reconcile && (
        <Notice
          kind={reconcile.consistent ? 'success' : 'error'}
          onClose={() => setReconcile(null)}
        >
          {reconcile.consistent
            ? 'Текущие остатки совпадают с суммой журнала движений.'
            : `Расхождений: ${reconcile.count}. Это дефект — требуется расследование.`}
        </Notice>
      )}

      <div className="mb-4 flex flex-wrap items-end gap-2">
        <label className="block">
          <span className="mb-1 block text-xs text-ink-500">С даты</span>
          <Input
            type="date"
            value={String(filter.from ?? '')}
            onChange={(e) => patch({ from: e.target.value })}
            className="w-40"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs text-ink-500">По дату</span>
          <Input
            type="date"
            value={String(filter.to ?? '')}
            onChange={(e) => patch({ to: e.target.value })}
            className="w-40"
          />
        </label>
        <LabeledSelect
          label="Склад"
          value={String(filter.warehouse_id ?? '')}
          onChange={(v) => patch({ warehouse_id: v })}
          options={[
            { value: '', label: 'Все' },
            ...warehouses.map((w) => ({ value: String(w.id), label: w.name })),
          ]}
        />
        <LabeledSelect
          label="Причина"
          value={String(filter.reason ?? '')}
          onChange={(v) => patch({ reason: v })}
          options={[
            { value: '', label: 'Все' },
            ...reasons.map((r) => ({ value: r.code, label: r.name_ru })),
          ]}
        />
      </div>

      {rows === null ? (
        <Spinner label="Загрузка журнала…" />
      ) : rows.length === 0 ? (
        <EmptyState
          title="Движений не найдено"
          hint="Измените условия отбора. Первые движения появятся после проводки прихода."
        />
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-ink-200 dark:border-ink-800">
            <table className="w-full min-w-[58rem] text-left text-sm">
              <thead className="border-b border-ink-200 bg-ink-50 text-xs uppercase tracking-wide text-ink-500 dark:border-ink-800 dark:bg-ink-900">
                <tr>
                  <th className="px-3 py-2.5 font-medium">Дата и время</th>
                  <th className="px-3 py-2.5 font-medium">Товар</th>
                  <th className="px-3 py-2.5 font-medium">Склад</th>
                  <th className="px-3 py-2.5 font-medium">Причина</th>
                  <th className="px-3 py-2.5 text-right font-medium">Дельта</th>
                  <th className="px-3 py-2.5 font-medium">Комментарий</th>
                  <th className="px-3 py-2.5 font-medium">Пользователь</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-200 dark:divide-ink-800">
                {rows.map((m) => (
                  <tr key={m.id} className="hover:bg-ink-50 dark:hover:bg-ink-900">
                    <td className="whitespace-nowrap px-3 py-2 tabular-nums text-xs text-ink-500">
                      {m.created_at}
                    </td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() => nav(`/products/${m.product_id}`)}
                        className="text-left text-ink-800 hover:text-brand-600 dark:text-ink-200"
                      >
                        {m.product_name}
                      </button>
                      <div className="font-mono text-xs text-ink-400">{m.product_sku}</div>
                    </td>
                    <td className="px-3 py-2 text-ink-600 dark:text-ink-400">{m.warehouse_name}</td>
                    <td className="px-3 py-2 text-ink-600 dark:text-ink-400">{m.reason_name}</td>
                    <td
                      className={`px-3 py-2 text-right tabular-nums font-medium ${
                        m.delta > 0 ? 'text-success-700 dark:text-success-100' : 'text-danger-700 dark:text-danger-100'
                      }`}
                    >
                      {m.delta > 0 ? '+' : ''}
                      {m.delta}
                    </td>
                    <td className="max-w-56 truncate px-3 py-2 text-xs text-ink-500">
                      {m.comment}
                    </td>
                    <td className="px-3 py-2 text-xs text-ink-500">{m.user_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-3 flex items-center justify-end gap-2 text-sm">
            <Button
              size="sm"
              disabled={Number(filter.page ?? 1) <= 1}
              onClick={() => patch({ page: Number(filter.page ?? 1) - 1 })}
            >
              Назад
            </Button>
            <span className="text-ink-500">
              {filter.page ?? 1} из {pages}
            </span>
            <Button
              size="sm"
              disabled={Number(filter.page ?? 1) >= pages}
              onClick={() => patch({ page: Number(filter.page ?? 1) + 1 })}
            >
              Вперёд
            </Button>
          </div>
        </>
      )}
    </section>
  )
}

/**
 * Список выбора с подписью — обёртка над общим Select.
 *
 * Метрики берутся из общего органа управления, здесь только подпись
 * и приведение значения.
 */
function LabeledSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: string
  options: { value: string; label: string }[]
  onChange: (v: string) => void
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs text-ink-500">{label}</span>
      <Select value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </Select>
    </label>
  )
}
